// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC, type ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { SubmitButton } from './submit-button'

const SIZE_CLASS = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
} as const

export interface FormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  onSubmit: () => void | Promise<void>
  submitting?: boolean
  canSubmit?: boolean
  submitLabel?: ReactNode
  cancelLabel?: ReactNode
  size?: keyof typeof SIZE_CLASS
  submitTestId?: string
  children: ReactNode
}

export const FormDialog: FC<FormDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  submitting = false,
  canSubmit = true,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  size = 'lg',
  submitTestId,
  children,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cap the dialog height so long bodies scroll instead of pushing the
          header and footer off-screen. */}
      <DialogContent className={`${SIZE_CLASS[size]} max-h-[85vh] flex flex-col`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-2 flex-1 min-h-0 overflow-y-auto pr-1">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {cancelLabel}
          </Button>
          <SubmitButton
            onClick={onSubmit}
            loading={submitting}
            disabled={!canSubmit || submitting}
            data-testid={submitTestId}
          >
            {submitLabel}
          </SubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
